import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useMultishift, Type } from 'multishift';
import { useState, useCallback, useMemo } from 'react';
import { useCommands } from '@remirror/react-core';
import { useKeymap } from '../use-keymap/dist/remirror-react-hooks-use-keymap.browser.esm.js';
import { useKeymaps } from '../use-keymaps/dist/remirror-react-hooks-use-keymaps.browser.esm.js';
/**
 * Get the next index from an arrow key press.
 */

function indexFromArrowPress(_ref) {
  var direction = _ref.direction,
      matchLength = _ref.matchLength,
      previousIndex = _ref.previousIndex;
  return direction === 'next' ? previousIndex + 1 > matchLength - 1 ? 0 : previousIndex + 1 : previousIndex - 1 < 0 ? matchLength - 1 : previousIndex - 1;
}

var DEFAULT_DISMISS_KEYS = ['Escape', 'Tab', 'Shift-Tab'];
var DEFAULT_SUBMIT_KEYS = ['Enter'];
/**
 * This hook provides the primitives for rendering a dropdown menu within
 */

function useMenuNavigation(props) {
  var items = props.items,
      _props$direction = props.direction,
      direction = _props$direction === void 0 ? 'vertical' : _props$direction,
      isOpen = props.isOpen,
      onDismiss = props.onDismiss,
      onSubmit = props.onSubmit,
      _props$focusOnClick = props.focusOnClick,
      focusOnClick = _props$focusOnClick === void 0 ? true : _props$focusOnClick,
      _props$dismissKeys = props.dismissKeys,
      dismissKeys = _props$dismissKeys === void 0 ? DEFAULT_DISMISS_KEYS : _props$dismissKeys,
      _props$submitKeys = props.submitKeys,
      submitKeys = _props$submitKeys === void 0 ? DEFAULT_SUBMIT_KEYS : _props$submitKeys;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      index = _useState2[0],
      setIndex = _useState2[1];

  var _useCommands = useCommands(),
      focus = _useCommands.focus;

  var nextShortcut = direction === 'vertical' ? 'ArrowDown' : 'ArrowRight';
  var previousShortcut = direction === 'vertical' ? 'ArrowUp' : 'ArrowLeft';

  var _useMultishift = useMultishift({
    items: items,
    isOpen: isOpen,
    highlightedIndexes: 0 <= index && index < items.length ? [index] : [],
    type: Type.ControlledMenu
  }),
      getMenuProps = _useMultishift.getMenuProps,
      _getItemProps = _useMultishift.getItemProps,
      hoveredIndex = _useMultishift.hoveredIndex,
      itemIsSelected = _useMultishift.itemIsSelected,
      indexIsSelected = _useMultishift.indexIsSelected,
      indexIsHovered = _useMultishift.indexIsHovered,
      itemIsHovered = _useMultishift.itemIsHovered;
  /**
   * Callback used when pressing the next arrow key.
   */


  var homeCallback = useCallback(function () {
    if (!isOpen) {
      return false;
    }

    if (index !== 0) {
      setIndex(0);
    }

    return true;
  }, [index, isOpen]);
  /**
   * Callback used when pressing the next arrow key.
   */

  var endCallback = useCallback(function () {
    if (!isOpen) {
      return false;
    }

    if (index === items.length - 1) {
      setIndex(items.length - 1);
    }

    return true;
  }, [items, index, isOpen]);
  /**
   * Callback used when pressing the next arrow key.
   */

  var nextCallback = useCallback(function () {
    if (!isOpen) {
      return false;
    }

    setIndex(indexFromArrowPress({
      direction: 'next',
      matchLength: items.length,
      previousIndex: index
    }));
    return true;
  }, [items, index, isOpen]);
  /**
   * Callback used when pressing the previous arrow key.
   */

  var previousCallback = useCallback(function () {
    if (!isOpen) {
      return false;
    }

    setIndex(indexFromArrowPress({
      direction: 'previous',
      matchLength: items.length,
      previousIndex: index
    }));
    return true;
  }, [items, index, isOpen]);
  var submitCallback = useCallback(function () {
    var item = items[index];

    if (!isOpen || !item) {
      return false;
    }

    return onSubmit(item, 'keyPress');
  }, [index, isOpen, items, onSubmit]);
  var dismissCallback = useCallback(function () {
    if (!isOpen) {
      return false;
    }

    return onDismiss();
  }, [isOpen, onDismiss]);
  /**
   * Automatically select the item when clicked.
   */

  var getItemProps = useCallback(function (itemProps) {
    return _objectSpread({}, _getItemProps(_objectSpread(_objectSpread({}, itemProps), {}, {
      onClick: function onClick(event) {
        var _itemProps$onClick;

        (_itemProps$onClick = itemProps.onClick) === null || _itemProps$onClick === void 0 ? void 0 : _itemProps$onClick.call(itemProps, event);
        onSubmit(itemProps.item, 'click');

        if (focusOnClick) {
          focus();
        }
      }
    })));
  }, [_getItemProps, onSubmit, focus, focusOnClick]);
  var submitBindings = useMemo(function () {
    var bindings = {};

    var _iterator = _createForOfIteratorHelper(submitKeys),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var key = _step.value;
        bindings[key] = submitCallback;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return bindings;
  }, [submitCallback, submitKeys]);
  var dismissBindings = useMemo(function () {
    var bindings = {};

    var _iterator2 = _createForOfIteratorHelper(dismissKeys),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var key = _step2.value;
        bindings[key] = dismissCallback;
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    return bindings;
  }, [dismissCallback, dismissKeys]); // Navigation callbacks

  useKeymap(nextShortcut, nextCallback);
  useKeymap(previousShortcut, previousCallback);
  useKeymap('Home', homeCallback);
  useKeymap("Cmd-".concat(nextShortcut), homeCallback);
  useKeymap('End', nextCallback);
  useKeymap("Cmd-".concat(previousShortcut), endCallback); // Handle the submit keybindings

  useKeymaps(submitBindings); // Handle the dismiss bindings.

  useKeymaps(dismissBindings);
  return useMemo(function () {
    return {
      getMenuProps: getMenuProps,
      getItemProps: getItemProps,
      hoveredIndex: hoveredIndex,
      indexIsSelected: indexIsSelected,
      itemIsSelected: itemIsSelected,
      indexIsHovered: indexIsHovered,
      itemIsHovered: itemIsHovered,
      index: index,
      setIndex: setIndex
    };
  }, [getItemProps, getMenuProps, hoveredIndex, indexIsHovered, indexIsSelected, itemIsHovered, itemIsSelected, index]);
}

export { indexFromArrowPress as i, useMenuNavigation as u };